import { observable, action } from "mobx";
import Autobind from "autobind-decorator";
import RootStore from "./rootStore";
import GridLayoutModel from "models/gridLayoutModel";
import DCMViewModel from "models/DCMViewModel";
import {
  getEnabledElementById,
  getEnabledElements,
  clearEnabledElement,
} from "component/toolManager/enabledElements.js";
import { getDefaultViewport } from "component/molecules/viewport/ViewportConfig";
import Cache from "core/cache/index";
import { clearElementState, clearAll } from "component/toolManager/toolState";
import Math from "core/math";
import Config from "config";
import Constants from "constants/index";

import triggerEvent from "core/customEvent/triggerEvent.js";
import _ from "lodash";
/**
 * @class WorklistStore
 * @description 요청에 따른 Study 목록을 반환
 */
@Autobind
class GridLayoutStore {
  _rootStore: RootStore; /// rootStore 객체. 다른 Store를 연계할 때 사용

  _gridLayoutModel: GridLayoutModel = new GridLayoutModel();

  @observable _previousActiveDCMViewIndex: number = 0;
  @observable _previousDCMViewOrderCache: number = -1;

  @observable _DCMViewOrder: any = [];
  @observable _DCMViewModelMap: Map<number, any> = new Map();

  @observable _focusDCMViewIdx: number = -1;
  @observable _mainActiveViewIdx: number = 0; // -1 => 메인 선택 뷰 없음

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;
    if (Config.control.AllocateAllFramesOnLayout === false) {
      this._gridLayoutModel.setCurrentBaseLayout(1, 1);
    } else {
      this._gridLayoutModel.setCurrentBaseLayout(3, 4);
    }

    this.initialize();
  }

  @action
  initialize() {
    const maxRows = this._gridLayoutModel.maxBaseRows;
    const maxColumns = this._gridLayoutModel.maxBaseColumns;
    const maxPages = this._gridLayoutModel.totalPages;

    const numOfDCMView = maxRows * maxColumns * maxPages;
    this._DCMViewOrder = [];
    for (let i = 0; i < numOfDCMView; i++) {
      this._DCMViewOrder.push(i);
    }
    this._DCMViewModelMap.clear();
  }

  getLayoutMaxRows() {
    return this._gridLayoutModel.maxBaseRows;
  }

  getLayoutMaxColumns() {
    return this._gridLayoutModel.maxBaseColumns;
  }

  getMaxLayout() {
    return {
      rows: this._gridLayoutModel.maxBaseRows,
      columns: this._gridLayoutModel.maxBaseColumns,
    };
  }

  getLayoutRows() {
    return this._gridLayoutModel.currentBaseRows;
  }

  getLayoutColumns() {
    return this._gridLayoutModel.currentBaseColumns;
  }

  getLayout() {
    return {
      rows: this._gridLayoutModel.currentBaseRows,
      columns: this._gridLayoutModel.currentBaseColumns,
      pages: this._gridLayoutModel.totalPages,
    };
  }
  @action
  expandLayoutToRow() {
    const {
      currentBaseRows,
      currentBaseColumns,
      maxBaseRows,
      totalPages,
    } = this._gridLayoutModel;

    const changeRows = Math.range.clip(currentBaseRows + 1, 1, maxBaseRows);
    this.changeLayout(changeRows, currentBaseColumns, totalPages);
  }
  @action
  collapseLayoutToRow() {
    const {
      currentBaseRows,
      currentBaseColumns,
      maxBaseRows,
      totalPages,
    } = this._gridLayoutModel;

    const changeRows = Math.range.clip(currentBaseRows - 1, 1, maxBaseRows);
    this.changeLayout(changeRows, currentBaseColumns, totalPages);
  }
  @action
  expandLayoutToColumn() {
    const {
      currentBaseRows,
      currentBaseColumns,
      maxBaseColumns,
      totalPages,
    } = this._gridLayoutModel;

    const changeColumns = Math.range.clip(
      currentBaseColumns + 1,
      1,
      maxBaseColumns
    );
    this.changeLayout(currentBaseRows, changeColumns, totalPages);
  }
  @action
  collapseLayoutToColumn() {
    const {
      currentBaseRows,
      currentBaseColumns,
      maxBaseColumns,
      totalPages,
    } = this._gridLayoutModel;

    const changeColumns = Math.range.clip(
      currentBaseColumns - 1,
      1,
      maxBaseColumns
    );
    this.changeLayout(currentBaseRows, changeColumns, totalPages);
  }

  getNumberOfViews() {
    return (
      this._gridLayoutModel.currentBaseRows *
      this._gridLayoutModel.currentBaseColumns
    );
  }

  getLayoutTotalPages() {
    return this._gridLayoutModel.totalPages;
  }

  @action
  setLayoutTotalPages(totalPages: number) {
    this._gridLayoutModel.totalPages = totalPages;
    //this.initialize();
    //this.clearAll();
    // Action에 대한 효율적인 Transaction 단위 정리 필요
    const maxRows = this._gridLayoutModel.maxBaseRows;
    const maxColumns = this._gridLayoutModel.maxBaseColumns;
    const maxPages = this._gridLayoutModel.totalPages;

    const numOfDCMView = maxRows * maxColumns * maxPages;
    this._DCMViewOrder = [];
    for (let i = 0; i < numOfDCMView; i++) {
      this._DCMViewOrder.push(i);
    }
    this._DCMViewModelMap.clear();

    // 모든 DCMView Measure Tool 정보 제거
    clearAll();
    // all viewport reset
    let entireEnabledElements = getEnabledElements();

    for (let i = 0; i < entireEnabledElements.length; i++) {
      entireEnabledElements[i].viewport = getDefaultViewport(
        null as any,
        undefined as any
      );
    }
  }

  @action
  focusOneLayout(targetDCMViewIdx: number) {
    if (this._focusDCMViewIdx !== targetDCMViewIdx) {
      this._focusDCMViewIdx = targetDCMViewIdx;
    } else {
      this._focusDCMViewIdx = -1;
    }
  }

  getFocusOneDCMViewIdx() {
    return this._focusDCMViewIdx;
  }

  // @action
  // focusOneLayout(targetDCMIndex: number) {
  //   const currentLayoutRows = this._gridLayoutModel.currentBaseRows;
  //   const currentLayoutColumns = this._gridLayoutModel.currentBaseColumns;

  //   const previousDCMViewRows = this._gridLayoutModel.previousBaseRows;
  //   const previousDCMViewColumns = this._gridLayoutModel.previousBaseColumns;

  //   if (currentLayoutRows === 1 && currentLayoutColumns === 1) {
  //     if (previousDCMViewRows === -1 && previousDCMViewColumns === -1) {
  //       return;
  //     }
  //     /// rollback rendermap
  //     this._gridLayoutModel.setCurrentBaseLayout(
  //       previousDCMViewRows,
  //       previousDCMViewColumns
  //     );

  //     // change renderMap
  //     this._DCMViewOrder[0] = 0;
  //     this._DCMViewOrder[
  //       this._previousDCMViewOrderCache
  //     ] = this._previousDCMViewOrderCache;

  //     this._previousDCMViewOrderCache = -1;
  //     /// reset related state
  //     this._gridLayoutModel.setPreviousBaseLayout(-1, -1);
  //   } else {
  //     /// Enter 1x1 viewport layout
  //     // backup current layout
  //     this._gridLayoutModel.setPreviousBaseLayout(
  //       currentLayoutRows,
  //       currentLayoutColumns
  //     );
  //     this._previousDCMViewOrderCache = targetDCMIndex;

  //     // set 1x1 layout
  //     this._gridLayoutModel.setCurrentBaseLayout(1, 1);

  //     // change renderMap
  //     this._DCMViewOrder[0] = this._previousDCMViewOrderCache;
  //     this._DCMViewOrder[this._previousDCMViewOrderCache] = 0;
  //   }
  // }

  @action
  changeLayout(rows: number, columns: number, pages: number = 1) {
    const previousDCMViewRows = this._gridLayoutModel.previousBaseRows;
    const DCMViewColumns = this._gridLayoutModel.currentBaseColumns;

    if (previousDCMViewRows !== -1 && DCMViewColumns !== -1) {
      //const viewportIndex = this._previousActiveDCMViewIndex;
      const viewportIndex = this._DCMViewOrder[
        this._previousActiveDCMViewIndex
      ];

      // change renderMap
      this._DCMViewOrder[0] = 0;
      this._DCMViewOrder[viewportIndex] = viewportIndex;

      /// init relative state
      this._gridLayoutModel.previousBaseRows = -1;
      this._gridLayoutModel.previousBaseColumns = -1;
    }

    // 1. 현재 레이아웃 정보를 이전 레이아웃 정보에 저장
    const currentRows = this._gridLayoutModel.currentBaseRows;
    const currentColumns = this._gridLayoutModel.currentBaseColumns;
    this._gridLayoutModel.setPreviousBaseLayout(currentRows, currentColumns);
    // 2. 현재 레이아웃 정보 변경
    this._gridLayoutModel.setCurrentBaseLayout(rows, columns);

    // 3. DCMView 페이지 개수 변경
    this._gridLayoutModel.totalPages = pages;
  }

  redrawAllDCMView() {
    const maxRows = this._gridLayoutModel.maxBaseRows;
    const maxColumns = this._gridLayoutModel.maxBaseColumns;

    const numOfDCMView = maxRows * maxColumns;

    for (let i = 0; i < numOfDCMView; i++) {
      let enabledElement = getEnabledElementById(i);
      if (enabledElement === null || enabledElement === undefined) {
        return;
      }
      enabledElement.needsRedraw = true;
    }
  }

  invalidateDCMView(DCMViewIndex: number) {
    let enabledElement = getEnabledElementById(DCMViewIndex);

    if (enabledElement === null || enabledElement === undefined) {
      return;
    }
    enabledElement.needsRedraw = true;
  }

  getDCMViewOrder(DCMViewIndex: number) {
    if (!(DCMViewIndex < this._DCMViewOrder.length)) {
      return null;
    }

    return this._DCMViewOrder[DCMViewIndex];
  }

  @action
  setActiveOnlyOneDCMView(DCMViewIndex: number) {
    //실험
    this._mainActiveViewIdx = DCMViewIndex;
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (key !== DCMViewIndex) {
        value.isActiveState = false;
      } else {
        value.isActiveState = true;
      }
    });

    // 활성화된 DCMView에 대한 ImageFusion 버튼 활성상태 갱신
    this._rootStore.toolbarStore.checkImageFusionState();
  }

  @action
  toggleActiveOnlyOneDCMView(DCMViewIndex: number) {
    this._mainActiveViewIdx = DCMViewIndex;
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (key !== DCMViewIndex) {
        value.isActiveState = false;
      } else {
        value.isActiveState = !value.isActiveState;
      }
    });
  }
  @action
  toggleActiveDCMView(DCMViewIndex: number) {
    if (this._mainActiveViewIdx === DCMViewIndex) {
      this._mainActiveViewIdx = -1;
    }
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (key === DCMViewIndex) {
        value.isActiveState = !value.isActiveState;
      }
    });
  }
  @action
  setActiveDCMViewIndex(DCMViewIndex: number) {
    let isChanged = false;
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (key === DCMViewIndex) {
        if (value.isActiveState === false) {
          isChanged = true;
        }
        value.isActiveState = true;
      }
    });
    return isChanged;
  }
  // getActiveDCMViewIndex() {
  //   return this._activeDCMViewIndex;
  // }

  // @action
  // setActiveDCMViewIndex(DCMViewIndex: number) {
  //   this._activeDCMViewIndex = DCMViewIndex;
  // }

  @action
  setMetadataOnViewAsList(assignmentList: Array<Object>) {
    /// 1. 초기화
    const maxRows = this._gridLayoutModel.maxBaseRows;
    const maxColumns = this._gridLayoutModel.maxBaseColumns;
    const maxPages = this._gridLayoutModel.totalPages;

    const numOfDCMView = maxRows * maxColumns * maxPages;
    this._DCMViewOrder = [];
    for (let i = 0; i < numOfDCMView; i++) {
      this._DCMViewOrder.push(i);
    }
    this._DCMViewModelMap.clear();

    /// 2.할당
    let activeIdx = 0;
    for (let i = 0; i < assignmentList.length; i++) {
      const metadataUID = (assignmentList[i] as any)["MetadataUID"];
      const DCMViewIdx = (assignmentList[i] as any)["DCMViewIdx"];
      const frameIdx = (assignmentList[i] as any)["frameIdx"];

      const isActive = (assignmentList[i] as any)["active"];
      const isBatchMode = (assignmentList[i] as any)["BatchMode"];
      const isMPR = (assignmentList[i] as any)["MPR"];
      const colorBox = (assignmentList[i] as any)["colorBox"] || null;

      const imageOrientation =
        (assignmentList[i] as any)["imageOrientation"] || "axial";
      const showTagOverlay = _.get(
        assignmentList[i] as any,
        "showTagOverlay",
        false
      );
      const onInitCallback = _.get(
        assignmentList[i] as any,
        "onInitCallback",
        false
      );

      if (isActive === true) {
        activeIdx = i;
      }
      let enabledElement = getEnabledElementById(DCMViewIdx);
      if (enabledElement === undefined && enabledElement === null) {
        enabledElement.imageIdx = frameIdx;
      }

      let dcmViewModel: DCMViewModel = new DCMViewModel();
      dcmViewModel.setDCMViewModel(
        metadataUID,
        0,
        frameIdx,
        "Contrast",
        "",
        isBatchMode,
        imageOrientation,
        showTagOverlay,
        isMPR,
        colorBox,
        onInitCallback
      );
      this._DCMViewModelMap.set(DCMViewIdx, dcmViewModel);
    }

    if (assignmentList.length > 0) {
      this._rootStore.gridLayoutStore.setActiveDCMViewIndex(activeIdx);
      this._rootStore.seriesListStore.setActiveSeriesItem(
        (assignmentList[0] as any)["MetadataUID"]
      );
      if (
        this._rootStore.synchronizationStore.isSupportedSynchronizationOnActive() ===
        true
      ) {
        this._rootStore.synchronizationStore.reactiveSyncState();
      } else {
        this._rootStore.synchronizationStore.disableSyncState();
      }
    }
  }

  @action
  setSeriesMetadataAtDCMView(
    DCMViewIndex: number,
    seriesID: string,
    displayFrameIdx: number,
    isBatchMode: boolean
  ) {
    let foundIdx = -1;
    this._DCMViewOrder.forEach(function (item: any, index: any) {
      if (DCMViewIndex === item) {
        foundIdx = index;
      }
    });

    if (foundIdx === -1) {
      return;
    }
    let dcmViewModel: DCMViewModel = new DCMViewModel();
    dcmViewModel.setDCMViewModel(
      seriesID,
      0,
      displayFrameIdx,
      "Contrast",
      "",
      isBatchMode
    );

    this._DCMViewModelMap.set(foundIdx, dcmViewModel);
    let enabledElement = getEnabledElementById(DCMViewIndex);

    enabledElement.imageIdx = displayFrameIdx;
    let metadataCache = new Cache.Metadata();
    //enabledElement.image = metadataCache.getFrame(seriesID, 0);
    enabledElement.image = metadataCache.getFrame(seriesID, displayFrameIdx);
    enabledElement.viewport = getDefaultViewport(null as any, undefined as any);
    this.setActiveDCMViewIndex(DCMViewIndex);
    const data = {
      //newImageIndex: 0,
      newImageIndex: displayFrameIdx,
      noSync: true,
      data: seriesID,
    };
    //triggerEvent(enabledElement.element, "onChangeScrollbar", data);
    enabledElement.needsRedraw = true;
  }
  getActiveDCMViews() {
    let activeStateIdx: number[] = [];

    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (value.isActiveState === true) {
        if (key !== -1) {
          activeStateIdx.push(key);
        }
      }
    });

    if (activeStateIdx.includes(this._mainActiveViewIdx) === false) {
      if (this._mainActiveViewIdx !== -1) {
        activeStateIdx.push(this._mainActiveViewIdx);
      }
    }

    return activeStateIdx;
    // let activeDCMViews: any[] = [];

    // this._DCMViewModelMap.forEach((value: any, key: any) => {
    //   const DCMViewModel = (this as any)._DCMViewModelMap.get(key);
    //   if (value.isActiveState === true) {
    //     const viewOrder = this._DCMViewOrder.get(key);
    //     let enabledElement = getEnabledElementById(viewOrder);
    //     if (activeDCMViews.includes(enabledElement) === false) {
    //       activeDCMViews.push(enabledElement);
    //     }
    //   }
    // });

    // const mainActiveElement = getEnabledElementById(this._mainActiveViewIdx);

    // if (activeDCMViews.includes(mainActiveElement) === false) {
    //   activeDCMViews.push(mainActiveElement);
    // }

    // return activeDCMViews;
  }

  isActiveDCMView(DCMViewIndex: number) {
    let isFound = false;
    let isActive = false;
    if (this._mainActiveViewIdx === DCMViewIndex) {
      return true;
    }
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (key === DCMViewIndex) {
        isFound = true;
        isActive = value.isActiveState;
      }
    });
    return isActive;
  }

  isShowTagOverlayOnDCMView(DCMViewIndex: number) {
    if (this._DCMViewModelMap.has(DCMViewIndex) === false) {
      return null;
    }
    return this._DCMViewModelMap.get(DCMViewIndex).showTagOverlay;
  }

  getMetadataIDOnDCMView(DCMViewIndex: number) {
    if (this._DCMViewModelMap.has(DCMViewIndex) === false) {
      return null;
    }
    return this._DCMViewModelMap.get(DCMViewIndex).metadataUID;
  }

  getDefaultFrameIdx(DCMViewIndex: number) {
    if (this._DCMViewModelMap.has(DCMViewIndex) === false) {
      return 0;
    }

    return this._DCMViewModelMap.get(DCMViewIndex).defaultFrameIdx;
  }

  getIsBatchMode(DCMViewIndex: number) {
    if (this._DCMViewModelMap.has(DCMViewIndex) === false) {
      return 0;
    }

    return this._DCMViewModelMap.get(DCMViewIndex).isBatchMode;
  }

  getIsMPR(DCMViewIndex: number) {
    if (this._DCMViewModelMap.has(DCMViewIndex) === false) {
      return false;
    }
    return this._DCMViewModelMap.get(DCMViewIndex).isMPR;
  }

  getColorBox(DCMViewIndex: number) {
    if (this._DCMViewModelMap.has(DCMViewIndex) === false) {
      return false;
    }
    return this._DCMViewModelMap.get(DCMViewIndex).colorBox;
  }

  getOnInitCallback(DCMViewIndex: number) {
    if (this._DCMViewModelMap.has(DCMViewIndex) === false) {
      return false;
    }
    return this._DCMViewModelMap.get(DCMViewIndex).onInitCallback;
  }

  getEmptyDCMViewIndex() {
    const DCMViewRows = this._gridLayoutModel.currentBaseRows;
    const DCMViewColumns = this._gridLayoutModel.currentBaseColumns;
    ///
    /// 1. 빈 DCMView를 찾는다.
    ///
    let isUsingView = [];
    for (let i = 0; i < DCMViewRows * DCMViewColumns; i++) {
      isUsingView.push(false);
    }

    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (key < isUsingView.length) isUsingView[key] = true;
    });

    let foundEmptyViewIndex = -1;

    for (let i = 0; i < DCMViewRows * DCMViewColumns; i++) {
      if (isUsingView[i] === true) {
        continue;
      }
      foundEmptyViewIndex = i;
      break;
    }

    return foundEmptyViewIndex;
  }

  getModalityForActiveDCMView(): string {
    const { metadataStore } = this._rootStore;
    let activeStateIdx: number[] = [];
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (value.isActiveState === true) {
        activeStateIdx.push(key);
      }
    });
    if (this._mainActiveViewIdx !== -1) {
      const enabledElement = getEnabledElementById(this._mainActiveViewIdx);
      if (enabledElement === null || enabledElement === undefined) {
        return "";
      }
      const scrollIndex = enabledElement.imageIdx;
      const dataKey = this.getMetadataIDOnDCMView(this._mainActiveViewIdx);
      if (dataKey === undefined || dataKey === null) {
        return "";
      }
      const modality = metadataStore.getModailty(dataKey, scrollIndex);
      return modality;
    }
    if (activeStateIdx.length === 0) {
      return "";
    }
    let activeFirstDCMViewIdx = activeStateIdx[0];
    const enabledElement = getEnabledElementById(activeFirstDCMViewIdx);
    if (enabledElement === null || enabledElement === undefined) {
      return "";
    }
    const scrollIndex = enabledElement.imageIdx;
    //const dataKey = this.getMetadataIDOnDCMView(this._activeDCMViewIndex);
    const dataKey = this.getMetadataIDOnDCMView(activeFirstDCMViewIdx);
    if (dataKey === undefined || dataKey === null) {
      return "";
    }
    const modality = metadataStore.getModailty(dataKey, scrollIndex);
    return modality;
  }

  getActiveDCMViewDump(): any {
    const { metadataStore } = this._rootStore;
    let activeStateIdx: number[] = [];
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (value.isActiveState === true) {
        activeStateIdx.push(key);
      }
    });

    if (activeStateIdx.length === 0) {
      return [];
    }
    let activeFirstDCMViewIdx = activeStateIdx[0];
    // 아래 코드 미동작.
    // const scrollIndex = this.getActiveDCMViewScroll();
    // if (scrollIndex === -1) {
    //   return [];
    // }
    //const enabledElement = getEnabledElementById(this._activeDCMViewIndex);
    const enabledElement = getEnabledElementById(activeFirstDCMViewIdx);
    if (enabledElement === null || enabledElement === undefined) {
      return [];
    }
    const scrollIndex = enabledElement.imageIdx;
    //const dataKey = this.getMetadataIDOnDCMView(this._activeDCMViewIndex);
    const dataKey = this.getMetadataIDOnDCMView(activeFirstDCMViewIdx);
    if (dataKey === undefined || dataKey === null) {
      return [];
    }
    const dump = metadataStore.dump(dataKey, scrollIndex);

    return dump;
    // 아래 코드 미동작.
    // const scrollIndex = this.getActiveDCMViewScroll();
    // if (scrollIndex === -1) {
    //   return [];
    // }
    // const enabledElement = getEnabledElementById(this._activeDCMViewIndex);
    // if (enabledElement === null || enabledElement === undefined) {
    //   return [];
    // }
    // const scrollIndex = enabledElement.imageIdx;
    // const dataKey = this.getMetadataIDOnDCMView(this._activeDCMViewIndex);
    // if (dataKey === undefined || dataKey === null) {
    //   return [];
    // }
    // const dump = metadataStore.dump(dataKey, scrollIndex);

    // return dump;
  }

  getActiveDCMViewPatientInfo(): any {
    const { metadataStore } = this._rootStore;
    // 아래 코드 미동작.
    // const scrollIndex = this.getActiveDCMViewScroll();
    // if (scrollIndex === -1) {
    //   return [];
    // }
    let activeStateIdx: number[] = [];
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (value.isActiveState === true) {
        activeStateIdx.push(key);
      }
    });

    if (activeStateIdx.length === 0) {
      return [];
    }
    let activeFirstDCMViewIdx = activeStateIdx[0];
    //const enabledElement = getEnabledElementById(this._activeDCMViewIndex);
    const enabledElement = getEnabledElementById(activeFirstDCMViewIdx);
    if (enabledElement === null || enabledElement === undefined) {
      return [];
    }
    const scrollIndex = enabledElement.imageIdx;
    //const dataKey = this.getMetadataIDOnDCMView(this._activeDCMViewIndex);
    const dataKey = this.getMetadataIDOnDCMView(activeFirstDCMViewIdx);
    if (dataKey === undefined || dataKey === null) {
      return [];
    }
    const dump = metadataStore.dump(dataKey, scrollIndex);

    const patientInfo = {
      modality: dump.modality,
      patientName: dump.patientName,
      studyDescription: dump.studyDescription,
    };

    return patientInfo;
  }

  @action
  setMousePositionOption(
    DCMViewIndex: number,
    mousePosX: number,
    mousePosY: number
  ) {
    if (this._DCMViewModelMap.has(DCMViewIndex) === false) {
      return;
    }
    this._DCMViewModelMap
      .get(DCMViewIndex)
      .setExtension("MousePosition", { x: mousePosX, y: mousePosY });
  }
  getMousePositionOption(DCMViewIndex: number) {
    if (this._DCMViewModelMap.has(DCMViewIndex) === false) {
      return "";
    }
    return this._DCMViewModelMap
      .get(DCMViewIndex)
      .getExtension("MousePosition");
  }

  getContrastOptionName(DCMViewIndex: number) {
    if (this._DCMViewModelMap.has(DCMViewIndex) === false) {
      return "";
    }
    return this._DCMViewModelMap.get(DCMViewIndex).getExtension("Contrast");
  }
  @action
  setContrastOptionNameOnActiveDCMView(optionName: string) {
    let activeStateIdx: number[] = [];
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (value.isActiveState === true) {
        activeStateIdx.push(key);
      }
    });

    if (activeStateIdx.length === 0) {
      return;
    }
    const activeFirstDCMViewIdx = activeStateIdx[0];

    if (this._DCMViewModelMap.has(activeFirstDCMViewIdx) === false) {
      return;
    }
    this._DCMViewModelMap
      .get(activeFirstDCMViewIdx)
      .setExtension("Contrast", optionName);
  }
  getContrastOptionNameOnActiveDCMView() {
    let activeStateIdx: number[] = [];
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (value.isActiveState === true) {
        activeStateIdx.push(key);
      }
    });

    if (activeStateIdx.length === 0) {
      return;
    }
    const activeFirstDCMViewIdx = activeStateIdx[0];

    if (this._DCMViewModelMap.has(activeFirstDCMViewIdx) === false) {
      return "";
    }
    return this._DCMViewModelMap
      .get(activeFirstDCMViewIdx)
      .getExtension("Contrast");
  }

  @action
  setMPROptionNameOnActiveDCMView(optionName: string) {
    let activeStateIdx: number[] = [];
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (value.isActiveState === true) {
        activeStateIdx.push(key);
      }
    });

    if (activeStateIdx.length === 0) {
      return;
    }
    const activeFirstDCMViewIdx = activeStateIdx[0];

    if (this._DCMViewModelMap.has(activeFirstDCMViewIdx) === false) {
      return;
    }
    this._DCMViewModelMap
      .get(activeFirstDCMViewIdx)
      .setExtension("MPR", optionName);
  }

  getMPROptionName(DCMViewIndex: number) {
    if (this._DCMViewModelMap.has(DCMViewIndex) === false) {
      return "";
    }
    return this._DCMViewModelMap.get(DCMViewIndex).getExtension("MPR");
  }

  getMPROptionNameOnActiveDCMView() {
    let activeStateIdx: number[] = [];
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (value.isActiveState === true) {
        activeStateIdx.push(key);
      }
    });

    if (activeStateIdx.length === 0) {
      return;
    }
    const activeFirstDCMViewIdx = activeStateIdx[0];

    if (this._DCMViewModelMap.has(activeFirstDCMViewIdx) === false) {
      return "";
    }
    return this._DCMViewModelMap.get(activeFirstDCMViewIdx).getExtension("MPR");
    //return this._DCMViewModelMap.get(activeFirstDCMViewIdx).getExtension("MPR")
    //  .value;
  }
  @action
  toggleNegativeOnActiveDCMView() {
    // const enabledElement = getEnabledElementById(this._activeDCMViewIndex);
    // if (enabledElement === null || enabledElement === undefined) {
    //   return false;
    // }
    // if (this._DCMViewModelMap.has(this._activeDCMViewIndex) === false) {
    //   return;
    // }
    // const test = this._DCMViewModelMap.get(this._activeDCMViewIndex).isNagative;
    // this._DCMViewModelMap.get(this._activeDCMViewIndex).isNagative = !test; //setExtension("Nagative", !test);
  }

  getIsNegativeOnActiveDCMView() {
    let activeStateIdx: number[] = [];
    this._DCMViewModelMap.forEach((value: any, key: any) => {
      if (value.isActiveState === true) {
        activeStateIdx.push(key);
      }
    });

    if (activeStateIdx.length === 0) {
      return;
    }
    const activeFirstDCMViewIdx = activeStateIdx[0];

    const enabledElement = getEnabledElementById(activeFirstDCMViewIdx);
    if (enabledElement === null || enabledElement === undefined) {
      return false;
    }

    if (this._DCMViewModelMap.has(activeFirstDCMViewIdx) === false) {
      return false;
    }

    const isNagative = this._DCMViewModelMap.get(activeFirstDCMViewIdx)
      .isNagative;

    if (isNagative === enabledElement.viewport.invert) {
      return isNagative;
    }
    // this._DCMViewModelMap.get(this._activeDCMViewIndex).isNagative =
    //   enabledElement.viewport.invert;
    return isNagative;
    //return enabledElement.viewport.invert;
  }

  @action
  activeMultiDCMView(
    modifiedKeys: {
      ctrlKey: boolean;
      altKey: boolean;
      shiftKey: boolean;
    },
    DCMViewIdx: number
  ) {
    let { altKey, ctrlKey, shiftKey } = modifiedKeys;
    const { synchronizationStore } = this._rootStore;
    const syncState = synchronizationStore.getSyncState();
    if (syncState !== Constants.Synchronization.SYNC_MANUAL) {
      ctrlKey = altKey = shiftKey = false;
    }
    const bothPressed = (ctrlKey && shiftKey) === true;
    if (bothPressed === true) {
      // shift + ctrl이 같이 눌러진 경우
      // 일반 적으로는
    } else if (bothPressed === false && shiftKey === true) {
      // shift key 만 눌러진 경우
      const activeDCMViewIdxes = this.getActiveDCMViews();
      let startActiveDCMViewIdx = 0;
      if (activeDCMViewIdxes.length > 0) {
        startActiveDCMViewIdx = activeDCMViewIdxes[0];
      }

      let startDCMViewIdx =
        startActiveDCMViewIdx > DCMViewIdx ? DCMViewIdx : startActiveDCMViewIdx;
      let endDCMViewIdx =
        startActiveDCMViewIdx > DCMViewIdx ? startActiveDCMViewIdx : DCMViewIdx;
      for (let i = startDCMViewIdx; i < endDCMViewIdx + 1; i++) {
        this.setActiveDCMViewIndex(i);
      }
      if (
        this._rootStore.synchronizationStore.getSyncState() === "sync-manual"
      ) {
        const targetDCMViewIds = this.getActiveDCMViews();
        this._rootStore.synchronizationStore.synchronizeManualElementByIds(
          targetDCMViewIds
        );
      }
    } else if (bothPressed === false && ctrlKey === true) {
      // ctrl key 만 눌러진경우
      this.toggleActiveDCMView(DCMViewIdx);
      if (
        this._rootStore.synchronizationStore.getSyncState() === "sync-manual"
      ) {
        const targetDCMViewIds = this.getActiveDCMViews();
        this._rootStore.synchronizationStore.synchronizeManualElementByIds(
          targetDCMViewIds
        );
      }
    } else {
      // 이외의 경우는 기본 DCMView 선택 동작을 수행
      this.setActiveOnlyOneDCMView(DCMViewIdx);

      if (
        this._rootStore.synchronizationStore.getSyncState() === "sync-manual"
      ) {
        const targetDCMViewIds = this.getActiveDCMViews();
        this._rootStore.synchronizationStore.synchronizeManualElementByIds(
          targetDCMViewIds
        );
      }
    }
  }

  @action
  remove(uniqueID: string) {
    let foundDCMViewIndex = -1;
    for (let [key, value] of (this._DCMViewModelMap as any).entries()) {
      if (value.metadataUID === uniqueID) {
        foundDCMViewIndex = key;
        this.clearDCMView(foundDCMViewIndex);
      }
    }
  }
  @action
  clearDCMView(DCMViewIndex: number) {
    const enabledElement = getEnabledElementById(DCMViewIndex);

    // DCMView Measure Tool 정보 제거
    clearElementState(enabledElement.element);
    // DCMView Store 정보 제거
    this._DCMViewModelMap.delete(DCMViewIndex); // = data.data;
    clearEnabledElement(enabledElement.element);
    // if (enabledElement) {
    //   enabledElement.image = null;
    //   enabledElement.needsRedraw = true;
    // }
  }

  @action
  clearAll() {
    this.initialize();
    // 모든 DCMView Measure Tool 정보 제거
    clearAll();
    // all viewport reset
    let entireEnabledElements = getEnabledElements();

    for (let i = 0; i < entireEnabledElements.length; i++) {
      clearEnabledElement(entireEnabledElements[i].element);
      // entireEnabledElements[i].viewport = getDefaultViewport(
      //   null as any,
      //   undefined as any
      // );
    }
  }
}

export default GridLayoutStore;
